/**
 * #483 Etappe 2: eine Liste aller Seiten außerhalb des Plans — speist die Verwaltung (2a), später Personen (2b),
 * Kopfzeile (2c), Benutzermenü (2d), Schnellzugriff (Etappe 3) und die Suche (Etappe 4).
 *
 * Reine Daten und reine Funktionen, wie `plan/planAktionen.ts`. Die Sichtbarkeit ist WÖRTLICH die von heute
 * (`components/AdminPage.tsx` vor V5.401.0): Wer eine Seite bisher sah, sieht sie weiter — niemand mehr, niemand weniger.
 * Einzige gewollte Änderung (Entscheidung 3): Wer nur eine Statistik-Freigabe hat, kommt jetzt wirklich an die
 * Statistik; vorher warf ihn die Seitensperre in App.tsx hinaus, weil sie nur die Rolle prüfte.
 *
 * Adressen sind flach (`/verwaltung/<slug>`, Entscheidung 6) — die Gruppe steht nur hier, damit spätere
 * Umsortierungen keine Links brechen. Alte Adressen stehen in `alteSlugs` und leiten um.
 */

import { darf, dienststellenFuer, FunktionsId, kannIrgendwo, karteiBearbeitenBereich, karteiVerwalten, RechteKontext, ueberallWirksam } from '../utils/rechte';
import { darfMonat, darfPlanungsrechner } from '../utils/nachbereitung';

export type BereichId = 'ereignisse' | 'personen' | 'anmeldungen' | 'abrechnung' | 'tools' | 'verwaltung';

export type VerwaltungsGruppeId = 'planung' | 'organisation' | 'email' | 'auswertung' | 'les' | 'system';

/** Wer eine Seite sieht — nur Wahrheitswerte, wie `PlanRechte`. */
export interface NavRechte {
  /** Freigaben (Statistik, Verteiler) laden asynchron; vorher wird nichts gesperrt oder umgeleitet. */
  geladen: boolean;
  isSuperAdmin: boolean;
  /** ADMIN oder SUPER_ADMIN */
  isAdmin: boolean;
  isEV: boolean;
  /** Freigabe je Benutzer (`HelperStatistikAccess`); Admins haben sie immer. */
  hasStatistikAccess: boolean;
  /** Freigabe für den E-Mail-Verteiler (`/email-pool/has-access`); Admins haben sie immer. */
  hasEmailAccess: boolean;
  /** DS-Admin mindestens einer Dienststelle (`dsDienststelleIds`), unabhängig von der Grundrolle. */
  isDSAdmin: boolean;
  /** Darf Nachrichten der Helfenden lesen (`darfLesen` aus `/nachrichten/ungelesen`). */
  nachrichtenLesbar: boolean;
  /** E7 (F23/F24): Rechte-Kontext des Kontos; fehlt er, gelten die festen Rollen wie bisher */
  kontext?: RechteKontext | null;
  /** #516: gebuchte/getestete Module; fehlt = noch unbekannt (dann nichts ausblenden) */
  module?: string[];
  /** #18: Module mit vorhandenen Daten — ohne Buchung lesbar (Monatsabschluss mit alten Abschlüssen) */
  bestand?: string[];
}

export type Zugang = 'alle' | 'verwaltung' | 'admin' | 'superAdmin' | 'statistik' | 'emailVerteiler' | 'nachrichten' | 'ankuendigungen' | 'anmeldungen' | 'infoseiten' | 'kalkulationen' | 'dienststellen' | 'vorlagen' | 'monat' | 'planungsrechner' | 'kartei' | 'registrierungen';

export interface Seite {
  /** Stabil — Schlüssel für Schnellzugriff und Suche. Nie umbenennen. */
  id: string;
  titel: string;
  /** Name eines Symbols aus `react-icons/md` (die Komponente wählt es aus). */
  symbol: string;
  bereich: BereichId;
  gruppe?: VerwaltungsGruppeId;
  slug: string;
  alteSlugs?: string[];
  zugang: Zugang;
  /** #516: nur mit diesem Modul (Super-Admins sehen die Seite trotzdem — mit Hinweis) */
  modul?: string;
  /** Ohne gebuchtes Modul für niemanden, auch nicht für Super-Admins — und solange der Stand unbekannt ist ebenfalls nicht */
  nurGebucht?: boolean;
  /** Für die Suche (Etappe 4): Begriffe ohne Namenstreffer, z. B. „smtp“ → E-Mail-Server. */
  suchbegriffe?: string[];
  /**
   * E10: Die Funktion(en) der Rollen & Rechte, die der Server für diese Seite verlangt (alle müssen erlaubt sein). Mit Rechte-Kontext entscheidet `darf`,
   * ohne Kontext (alte Antwort) der bisherige `zugang`. Die nie übertragbaren Funktionen (E-Mail, LES, Audit-Log, System-Backup, Module, Rollen) gelten nur für den Super-Admin.
   */
  recht?: FunktionsId | readonly FunktionsId[];
  /** E10: Die Seite zeigt auch ohne die Funktion, wer sie nach dem bisherigen Zugang sehen darf — dann nur zum Ansehen (Organisationen, Dienststellen: Ereignisverantwortliche lesen) */
  rechtOderZugang?: boolean;
}

export const VERWALTUNGS_GRUPPEN: { id: VerwaltungsGruppeId; titel: string; symbol: string }[] = [
  { id: 'planung', titel: 'Planung', symbol: 'MdAssignment' },
  { id: 'organisation', titel: 'Organisation', symbol: 'MdAccountTree' },
  { id: 'email', titel: 'E-Mail', symbol: 'MdEmail' },
  { id: 'auswertung', titel: 'Auswertung', symbol: 'MdBarChart' },
  { id: 'les', titel: 'LES & Schnittstellen', symbol: 'MdSettingsInputAntenna' },
  { id: 'system', titel: 'System', symbol: 'MdSettings' },
];

const v = (gruppe: VerwaltungsGruppeId, id: string, titel: string, symbol: string, zugang: Zugang, extra: Partial<Seite> = {}): Seite =>
  ({ id, titel, symbol, bereich: 'verwaltung', gruppe, slug: id, zugang, ...extra });

/** Reihenfolge = Reihenfolge in den Reitern (Master-Mockup `master.js`, GRUPPEN). */
export const VERWALTUNGS_SEITEN: Seite[] = [
  v('planung', 'vorlagen', 'Plan-Vorlagen', 'MdAssignment', 'vorlagen', { suchbegriffe: ['vorlage', 'templates', 'muster', 'plan kopieren', 'schablone'] }),
  v('planung', 'infoseiten-vorlagen', 'Infoseiten-Vorlagen', 'MdArticle', 'verwaltung', { modul: 'infoseite', suchbegriffe: ['infoseite', 'info', 'seite für helfende', 'vorlage', 'webseite'] }),
  v('planung', 'checklisten', 'Checklisten-Vorlagen', 'MdChecklist', 'verwaltung', { suchbegriffe: ['material', 'aufbau', 'checkliste', 'mitnehmen', 'packliste', 'ausrüstung'] }),
  v('planung', 'kategorien', 'Positions-Kategorien', 'MdLabel', 'verwaltung', { recht: 'vKategorien', suchbegriffe: ['sanlog', 'kategorie', 'positionstyp', 'funktion'] }),
  v('planung', 'qualifikationen', 'Qualifikationen', 'MdSchool', 'admin', { recht: 'vQualifikationen', suchbegriffe: ['quali', 'führung', 'ausbildung', 'rs', 'rh', 'san', 'nfs', 'notfallsanitäter', 'rettungssanitäter', 'sanitäter', 'zugführer', 'gruppenführer'] }),
  v('planung', 'stundensaetze', 'Stundensätze', 'MdEuro', 'verwaltung', { suchbegriffe: ['geld', 'lohn', 'abrechnung', 'stundenlohn', 'satz', 'vergütung', 'entschädigung', 'euro'] }),
  v('planung', 'jugendschutz', 'Jugendschutz', 'MdShield', 'superAdmin', { suchbegriffe: ['u18', 'minderjährig', 'jarbschg', 'jugend', 'alter', 'nachtarbeit'] }),
  // #643 (E8): Liste aller Einschränkungen „Soll nicht“ — nur Admin/Super-Admin
  v('planung', 'einschraenkungen', 'Einschränkungen', 'MdBlock', 'admin', { suchbegriffe: ['soll nicht', 'einschränkung', 'ausnahme', 'darf nicht', 'sperre', 'kategorie', 'el', 'einteilen', 'prüfung'] }),
  // #646: Liste aller Personen mit „Gut geeignet“ — nur Admin/Super-Admin (Gegenstück zu „Einschränkungen“)
  v('planung', 'geeignet', 'Gut geeignet', 'MdStar', 'admin', { suchbegriffe: ['geeignet', 'gut geeignet', 'besonders geeignet', 'eignung', 'bevorzugt', 'gut für', 'einteilen', 'kategorie', 'gf', 'el'] }),
  v('organisation', 'dienststellen', 'Dienststellen', 'MdPlace', 'dienststellen', { recht: 'vDienststellen', rechtOderZugang: true, suchbegriffe: ['ov', 'kostenstelle', 'dienststelle', 'einheit', 'bereitschaft', 'ds'] }),
  v('organisation', 'organisationen', 'Organisationen', 'MdAccountTree', 'verwaltung', { recht: 'vOrg', rechtOderZugang: true, suchbegriffe: ['ov', 'kv', 'hiorg', 'ortsverein', 'kreisverband', 'gliederung', 'verband', 'mhd', 'asb', 'juh'] }),
  v('organisation', 'fahrzeuge', 'Fahrzeuge', 'MdDirectionsCar', 'admin', { suchbegriffe: ['auto', 'kfz', 'rtw', 'ktw', 'nef', 'mtw', 'gw', 'fahrzeug', 'kennzeichen', 'funkrufname', 'funkruf'] }),
  v('email', 'email-server', 'E-Mail-Server', 'MdEmail', 'superAdmin', { recht: 'vEmail', alteSlugs: ['email', 'smtp'], suchbegriffe: ['smtp', 'mailserver', 'dkim', 'spf', 'dmarc', 'absender', 'mail'] }),
  v('email', 'mail-vorlagen', 'Mail-Vorlagen', 'MdDescription', 'superAdmin', { recht: 'vEmail', suchbegriffe: ['mail', 'text', 'vorlage', 'e-mail-text', 'nachrichtentext'] }),
  v('email', 'mail-benachrichtigungen', 'Benachrichtigungen', 'MdNotifications', 'superAdmin', { recht: 'vEmail', suchbegriffe: ['auslöser', 'mail', 'trigger', 'automatisch', 'versand'] }),
  v('email', 'verteiler-postfach', 'Verteiler-Postfach', 'MdMoveToInbox', 'superAdmin', { recht: 'vEmail', suchbegriffe: ['imap', 'postfach', 'eingang', 'weiterleitung'] }),
  v('email', 'email-protokoll', 'E-Mail-Protokoll', 'MdHistory', 'superAdmin', { recht: 'vEmail', suchbegriffe: ['log', 'protokoll', 'versand', 'zugestellt', 'mail log', 'gesendet'] }),
  v('auswertung', 'statistik', 'Helfer-Statistik', 'MdBarChart', 'statistik', { suchbegriffe: ['auswertung', 'einsätze', 'statistik', 'stunden', 'zahlen', 'bericht'] }),
  v('les', 'master-link', 'Master-Link', 'MdLink', 'superAdmin', { recht: 'vLes', alteSlugs: ['les-integration'], suchbegriffe: ['les', 'funk', 'master', 'einsatzleitung', 'zugang'] }),
  v('les', 'les-pruefung', 'LES-Prüfung', 'MdSettingsInputAntenna', 'superAdmin', { recht: 'vLes', suchbegriffe: ['les', 'funk', 'prüfung', 'benutzer prüfen'] }),
  v('les', 'einsatzstatus-api', 'EinsatzStatus-API', 'MdVpnKey', 'superAdmin', { recht: 'vLes', suchbegriffe: ['api', 'schlüssel', 'les', 'schnittstelle', 'key', 'token'] }),
  v('system', 'system', 'System-Status', 'MdMonitorHeart', 'superAdmin', { suchbegriffe: ['status', 'online', 'speicher', 'server', 'wer ist online', 'auslastung', 'dashboard'] }),
  v('system', 'dateien', 'Dateien', 'MdFolder', 'superAdmin', { suchbegriffe: ['upload', 'speicherplatz', 'datei', 'dokument', 'anhang'] }),
  v('system', 'backup', 'Backup', 'MdBackup', 'superAdmin', { recht: 'vSysBackup', alteSlugs: ['system-backup'], suchbegriffe: ['sicherung', 'wiederherstellen', 'restore', 'export', 'sichern'] }),
  v('system', 'wartung', 'Wartungsmodus', 'MdBuild', 'superAdmin', { suchbegriffe: ['deploy', 'neustart', 'update', 'wartung', 'hinweis'] }),
  v('system', 'fehler-log', 'Fehler-Log', 'MdBugReport', 'superAdmin', { suchbegriffe: ['log', 'fehler', 'error', 'absturz', 'bug'] }),
  v('system', 'github-meldungen', 'GitHub-Meldungen', 'MdCode', 'superAdmin', { suchbegriffe: ['github', 'issue', 'meldung', 'fehler', 'ticket', 'repo', 'token'] }),
  v('system', 'suche-ohne-treffer', 'Suchen ohne Treffer', 'MdSearchOff', 'superAdmin', { suchbegriffe: ['suche', 'suchbegriffe', 'nicht gefunden', 'synonyme', 'statistik'] }),
  v('system', 'audit-log', 'Audit-Log', 'MdSecurity', 'superAdmin', { recht: 'vAudit', suchbegriffe: ['log', 'protokoll', 'anmeldung', 'sicherheit', 'login', 'zugriff', 'wer hat'] }),
  v('system', 'what3words', 'what3words', 'MdMap', 'superAdmin', { modul: 'infoseite', suchbegriffe: ['karte', 'w3w', 'drei wörter', 'schlüssel', 'api', 'infoseite', 'lageplan', 'ort'] }),
  v('system', 'lizenz', 'Lizenz', 'MdVpnKey', 'superAdmin', { recht: 'vModule', suchbegriffe: ['lizenz', 'schlüssel', 'key', 'modul', 'module', 'testphase', 'buchen'] }),
  // Rollen & Rechte (Zusatzmodul): Rollen als Vorlagen; Super-Admins sehen die Seite auch ohne Modul (mit Hinweis)
  v('system', 'rollen-rechte', 'Rollen und Rechte', 'MdAdminPanelSettings', 'superAdmin', { recht: 'vRollen', modul: 'rollen-rechte', suchbegriffe: ['rollen', 'rechte', 'berechtigung', 'vorlage', 'matrix', 'eigene rolle', 'zugriff', 'konto', 'abweichung'] }),
  // #584 (V6.47.0): Ausgangszustand des Helfer-Fensters im Plan für alle ohne eigene Ansicht
  v('system', 'helfer-fenster', 'Helfer-Fenster', 'MdViewQuilt', 'superAdmin', { modul: 'erweiterte-helferansicht', nurGebucht: true, suchbegriffe: ['helfer-fenster', 'helferfenster', 'ansicht', 'standard', 'systemstandard', 'bausteine', 'zusammenfassung', 'raster', 'spalten', 'vorgabe'] }),
  v('system', 'branding', 'Branding', 'MdPalette', 'superAdmin', { recht: 'vBranding', suchbegriffe: ['logo', 'farbe', 'design', 'name', 'aussehen', 'white label'] }),
  // #643 (E9): Abstand und Prüfende — ADMIN und SUPER_ADMIN dürfen einstellen (Vorbild Demo-Modus)
  v('system', 'einschraenkungen-pruefung', 'Prüfung der Einschränkungen', 'MdRule', 'admin', { suchbegriffe: ['soll nicht', 'einschränkung', 'prüfung', 'prüfende', 'abstand', 'monate', 'erinnerung', 'fällig'] }),
  v('system', 'demo-modus', 'Demo-Modus', 'MdPlayCircle', 'admin', { suchbegriffe: ['demo', 'vorführung', 'test', 'beispiel'] }),
];

/** Sichtbarkeit — wörtlich wie `AdminPage` vor V5.401.0 (`istVerwaltung`, `adminOnly`, `superAdminOnly`, `statistikOnly`). */
/** E7 F23: Seite und Bereich „Anmeldungen“ — Freigeben irgendwo (Standard: Administration, Ereignisverantwortliche) */
const darfAnmeldungenSehen = (r: NavRechte): boolean => (r.kontext ? kannIrgendwo(r.kontext, 'aFreigeben') : r.isAdmin || r.isEV);

/** E7 F24: Infoseiten — Infoseite bearbeiten überall oder für mindestens eine Dienststelle (Standard: Administration, EV, DS-Admins) */
const darfInfoseitenSehen = (r: NavRechte): boolean => {
  if (!r.kontext) return r.isAdmin || r.isEV || r.isDSAdmin;
  if (ueberallWirksam(r.kontext, 'aInfoseite')) return true;
  const ds = dienststellenFuer(r.kontext, 'aInfoseite');
  return ds === 'alle' || ds.length > 0;
};

/** E8: Seite und Bereich „Abrechnung“ (Monatsabschluss) */
const darfMonatSehen = (r: NavRechte): boolean => (r.kontext ? darfMonat(r.kontext) : r.isAdmin);

export const darfSehen = (s: Seite, r: NavRechte): boolean => {
  if (s.modul && s.nurGebucht && !r.module?.includes(s.modul)) return false;
  if (s.modul && r.module && !r.module.includes(s.modul) && !r.isSuperAdmin && !r.bestand?.includes(s.modul)) return false;
  // E10: Verwaltungsseiten — mit Rechte-Kontext entscheidet die Funktion des Servers (wie `darf`), sonst der bisherige Zugang
  if (s.recht && r.kontext) {
    const funktionen = typeof s.recht === 'string' ? [s.recht] : s.recht;
    if (funktionen.every(f => darf(r.kontext!, f))) return true;
    if (!s.rechtOderZugang) return false;
  }
  switch (s.zugang) {
    // Jede Rolle der Verwaltungs-App (Helfende melden sich im Portal an und sehen diese Liste nie)
    case 'alle': return true;
    case 'verwaltung': return r.isAdmin || r.isEV;
    case 'admin': return r.isAdmin;
    // E9: Helferkartei — genau dann, wenn der Server die Liste liefert („Karteieinträge bearbeiten“, auch nur für den eigenen Bereich); ohne Kontext die Administration wie bisher
    case 'kartei': return r.kontext ? karteiBearbeitenBereich(r.kontext) !== null : r.isAdmin;
    case 'superAdmin': return r.isSuperAdmin;
    // E10: Portal-Konten — Registrierungen prüfen verlangt „Benutzer verwalten“ UND „Karteieinträge bearbeiten“ für die ganze Kartei (wie der Server); ohne Kontext die Administration
    case 'registrierungen': return r.kontext ? darf(r.kontext, 'vBenutzer') && darf(r.kontext, 'kBearbeiten') && karteiVerwalten(r.kontext) : r.isAdmin;
    case 'statistik': return r.hasStatistikAccess;
    // Personen (2b) — wie die bisherigen Einstiege: Knopf „Email-Verteiler“, Megafon, Sprechblase
    case 'emailVerteiler': return r.isAdmin || r.hasEmailAccess;
    case 'nachrichten': return r.nachrichtenLesbar;
    case 'ankuendigungen': return r.isAdmin || r.isDSAdmin;
    // V6.9.0 wie der Server (`darfVorlagen`): Admin, Ereignisverantwortliche, DS-Admins
    case 'infoseiten': return darfInfoseitenSehen(r);
    case 'anmeldungen': return darfAnmeldungenSehen(r);
    // #615 / E8: „Kostenkalkulation“ (bKalkulation) — im Standard Admin, Ereignisverantwortliche, DS-Admins; ohne Kontext wie bisher die Rollen
    case 'kalkulationen': return r.kontext ? darf(r.kontext, 'bKalkulation') : r.isAdmin || r.isEV || r.isDSAdmin;
    // E8: Monatsabschluss — „Monatsabschluss und Abrechnung“ UND „Ereignisse ansehen: überall“; ohne Kontext die Administration
    case 'monat': return darfMonatSehen(r);
    // E8: Planungsrechner — nie Helfende UND „Planungsrechner“; ohne Kontext alle Verwaltungsrollen wie bisher
    case 'planungsrechner': return r.kontext ? darfPlanungsrechner(r.kontext, r.kontext.grundrolle) : true;
    // #664: Plan-Vorlagen — Admin, Ereignisverantwortliche und DS-Admins (der Server prüft das Recht je Vorlage)
    case 'vorlagen': return r.isAdmin || r.isEV || r.isDSAdmin;
    // #643 (C4/E16): DS-Admins pflegen die aktiven Kategorien ihrer Dienststellen — die Seite ist für sie nur ansehen + Reiter „Kategorien“
    case 'dienststellen': return r.isAdmin || r.isEV || r.isDSAdmin;
  }
};

export const sichtbareVerwaltungsSeiten = (r: NavRechte): Seite[] => VERWALTUNGS_SEITEN.filter(s => darfSehen(s, r));

/** Gruppen mit ihren sichtbaren Seiten; leere Gruppen fallen weg. */
export const verwaltungsGruppen = (r: NavRechte) =>
  VERWALTUNGS_GRUPPEN
    .map(g => ({ ...g, seiten: VERWALTUNGS_SEITEN.filter(s => s.gruppe === g.id && darfSehen(s, r)) }))
    .filter(g => g.seiten.length > 0);

export const verwaltungsPfad = (s: Pick<Seite, 'slug'>) => `/verwaltung/${s.slug}`;

export type Aufloesung =
  | { art: 'warten' }
  | { art: 'seite'; seite: Seite }
  | { art: 'umleitung'; ziel: string }
  | { art: 'kein-zugriff'; ziel: string };

/**
 * Was unter `pathname` gezeigt wird. Alte Adressen und fehlende Rechte leiten um — auf die erste erlaubte Seite
 * (vorher fiel jeder unbekannte Pfad still auf „Vorlagen“ zurück; so landete die Tour „Benutzerverwaltung“ dort).
 * Ohne jede erlaubte Seite geht es zur Ereignisübersicht.
 */
const NACH_PERSONEN: Record<string, string> = {
  benutzer: '/personen/verwaltungskonten',
  nachrichten: '/personen/nachrichten',
};

export const verwaltungAufloesen = (pathname: string, r: NavRechte): Aufloesung => {
  if (!r.geladen) return { art: 'warten' };
  const erlaubt = sichtbareVerwaltungsSeiten(r);
  if (erlaubt.length === 0) return { art: 'kein-zugriff', ziel: '/events' };
  const slug = pathname.replace(/^\/verwaltung\/?/, '').split('/')[0] || '';
  // Gab es nie als Verwaltungsseite, stand aber in Doku und Tour — heute in Personen
  if (NACH_PERSONEN[slug]) return { art: 'umleitung', ziel: NACH_PERSONEN[slug] };
  const genau = VERWALTUNGS_SEITEN.find(s => s.slug === slug);
  if (genau && erlaubt.includes(genau)) return { art: 'seite', seite: genau };
  const alt = VERWALTUNGS_SEITEN.find(s => s.alteSlugs?.includes(slug));
  if (alt && erlaubt.includes(alt)) return { art: 'umleitung', ziel: verwaltungsPfad(alt) };
  return { art: 'umleitung', ziel: verwaltungsPfad(erlaubt[0]) };
};

// ---------- Personen (Schritt 2b) ----------

/**
 * Ein Ort für alle Menschen und Konten (User: „nicht Helfende und Verwaltung getrennt“). Vorher verteilt auf
 * „Benutzer“ (drei Reiter), den Knopf „Email-Verteiler“, die Sprechblase (Nachrichten) und das Megafon (Ankündigungen).
 */
const pers = (id: string, titel: string, symbol: string, zugang: Zugang, extra: Partial<Seite> = {}): Seite =>
  ({ id, titel, symbol, bereich: 'personen', slug: id, zugang, ...extra });

export const PERSONEN_SEITEN: Seite[] = [
  pers('kartei', 'Helferkartei', 'MdBadge', 'kartei', { suchbegriffe: ['helfer', 'kartei', 'duplikate', 'helfende', 'mitglieder', 'personen', 'stammdaten', 'doppelt'] }),
  pers('portal-konten', 'Portal-Konten', 'MdPeople', 'registrierungen', { suchbegriffe: ['registrierung', 'freigabe', 'helfer', 'portal', 'konto', 'login', 'anmeldung', 'freischalten'] }),
  pers('verwaltungskonten', 'Verwaltungskonten', 'MdVpnKey', 'admin', { recht: 'vBenutzer', suchbegriffe: ['benutzer', 'konto', 'rechte', 'rolle', 'admin', 'zugang', 'passwort zurücksetzen', 'users'] }),
  // Telefonbuch (#683, Modul `telefonbuch`): Meine Geräte, für Super-Admins zusätzlich Alle Geräte; sichtbar mit dem Recht „Telefonbuch abonnieren“
  pers('telefonbuch', 'Telefonbuch', 'MdContacts', 'admin', { recht: 'kTelefonbuch', modul: 'telefonbuch', suchbegriffe: ['telefonbuch', 'adressbuch', 'carddav', 'kontakte', 'handy', 'iphone', 'android', 'gerät', 'dav'] }),
  pers('email-verteiler', 'E-Mail-Verteiler', 'MdMail', 'emailVerteiler', { suchbegriffe: ['mail', 'rundmail', 'pool', 'verteiler', 'newsletter', 'rundschreiben'] }),
  pers('nachrichten', 'Nachrichten', 'MdForum', 'nachrichten', { suchbegriffe: ['chat', 'fragen', 'nachricht', 'posteingang', 'antworten'] }),
  pers('ankuendigungen', 'Ankündigungen', 'MdCampaign', 'ankuendigungen', { suchbegriffe: ['rundschreiben', 'info', 'ankündigung', 'mitteilung', 'news', 'alle helfer'] }),
];

export const sichtbarePersonenSeiten = (r: NavRechte): Seite[] => PERSONEN_SEITEN.filter(s => darfSehen(s, r));
export const personenPfad = (s: Pick<Seite, 'slug'>) => `/personen/${s.slug}`;

/** Wie `verwaltungAufloesen`, für `/personen/<slug>`. */
export const personenAufloesen = (pathname: string, r: NavRechte): Aufloesung => {
  if (!r.geladen) return { art: 'warten' };
  const erlaubt = sichtbarePersonenSeiten(r);
  if (erlaubt.length === 0) return { art: 'kein-zugriff', ziel: '/events' };
  const slug = pathname.replace(/^\/personen\/?/, '').split('/')[0] || '';
  const genau = erlaubt.find(s => s.slug === slug);
  return genau ? { art: 'seite', seite: genau } : { art: 'umleitung', ziel: personenPfad(erlaubt[0]) };
};

/**
 * Alte Adressen außerhalb von `/verwaltung` → neue. Lesezeichen, e2e (`/users`) und der Sprung aus der Glocke
 * (`/benutzer?tab=helfer`) dürfen nicht ins Leere laufen. `null` = keine alte Adresse.
 */
export const alteAdresseZiel = (pathname: string, search: string): string | null => {
  const pfad = pathname.replace(/\/+$/, '');
  if (pfad === '/benutzer' || pfad === '/users') {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'helfer') return '/personen/portal-konten';
    if (tab === 'kartei') return '/personen/kartei';
    return '/personen/verwaltungskonten';
  }
  if (pfad === '/email-verteiler') return '/personen/email-verteiler';
  return null;
};

// ---------- Bereiche der Kopfzeile (Schritt 2c) ----------

export interface Bereich { id: BereichId; titel: string; symbol: string; pfad: string }

/** Reihenfolge wie im Master-Mockup: Ereignisse · Personen · Anmeldungen · Abrechnung · Verwaltung; „Tools“ (V6.9.1) vor der Verwaltung. */
export const BEREICHE: Bereich[] = [
  { id: 'ereignisse', titel: 'Ereignisse', symbol: 'MdEventNote', pfad: '/events' },
  { id: 'personen', titel: 'Personen', symbol: 'MdPeople', pfad: '/personen' },
  { id: 'anmeldungen', titel: 'Anmeldungen', symbol: 'MdAssignment', pfad: '/anmeldungen' },
  { id: 'abrechnung', titel: 'Abrechnung', symbol: 'MdEuro', pfad: '/abrechnung' },
  { id: 'tools', titel: 'Tools', symbol: 'MdBuild', pfad: '/tools' },
  { id: 'verwaltung', titel: 'Verwaltung', symbol: 'MdSettings', pfad: '/verwaltung' },
];

/**
 * Welche Bereiche oben stehen — wie die bisherigen Knöpfe: Anmeldungen (vorher „Abfragelinks“) Admin/EV,
 * Abrechnung nur Admin, Personen/Verwaltung sobald dort mindestens eine Seite erlaubt ist.
 */
export const sichtbareBereiche = (r: NavRechte): Bereich[] => BEREICHE.filter(b => {
  switch (b.id) {
    case 'ereignisse': return true;
    case 'personen': return sichtbarePersonenSeiten(r).length > 0;
    case 'anmeldungen': return darfAnmeldungenSehen(r);
    // #18: ohne Modul „Monatsabschluss“ nur mit Altbestand (lesbar) oder für Super-Admins (mit Hinweis)
    case 'abrechnung': return darfMonatSehen(r) && (!r.module || r.module.includes('monatsabschluss') || r.isSuperAdmin || !!r.bestand?.includes('monatsabschluss'));
    // V6.9.1: nur, wenn mindestens ein Tool freigeschaltet ist (Zusatzmodule, `nurGebucht`)
    case 'tools': return sichtbareToolSeiten(r).length > 0;
    case 'verwaltung': return sichtbareVerwaltungsSeiten(r).length > 0;
  }
});

/** V6.9.1 Bereich „Tools“: die Zusatzmodule mit eigener Seite — je Reiter nur mit gebuchtem Modul und Recht. */
export const sichtbareToolSeiten = (r: NavRechte): Seite[] => WEITERE_SEITEN.filter(s => s.bereich === 'tools' && darfSehen(s, r));

/** Zu welchem Bereich eine Ansicht der App gehört (`view` aus App.tsx). */
export const bereichVonAnsicht = (view: string): BereichId | null => {
  switch (view) {
    case 'events': return 'ereignisse';
    case 'personen': return 'personen';
    case 'abfragelinks': return 'anmeldungen';
    case 'abrechnung': return 'abrechnung';
    case 'verwaltung': return 'verwaltung';
    case 'planungsrechner': return 'tools';
    case 'infoseiten': return 'tools';
    case 'kalkulationen': case 'kalkulation': return 'tools';
    case 'tools': return 'tools';
    default: return null;
  }
};

// ---------- Alle Seiten (Schnellzugriff, Etappe 3) ----------

/** Anmeldungen und Abrechnung sind je eine Seite ohne Unterseiten — für den Schnellzugriff wählbar (Entscheidung 1). */
export const WEITERE_SEITEN: Seite[] = [
  { id: 'anmeldungen', titel: 'Anmeldungen', symbol: 'MdAssignment', bereich: 'anmeldungen', slug: '', zugang: 'anmeldungen', suchbegriffe: ['abfragelink', 'formular', 'org-meldelink', 'anmeldung', 'registrierung', 'link', 'meldelink'] },
  { id: 'abrechnung', titel: 'Abrechnung', symbol: 'MdEuro', bereich: 'abrechnung', slug: '', zugang: 'monat', modul: 'monatsabschluss', suchbegriffe: ['monatsabschluss', 'geld', 'auszahlung', 'abrechnung', 'zahlung', 'überweisung', 'beleg', 'stunden'] },
  // Bereich „Tools“ (V6.9.1): Zusatzmodule, Adressen bleiben flach (/infoseiten, /planungsrechner); ohne Modul für niemanden.
  // Infoseiten-Verwaltung (V6.9.0) — alle Infoseiten, auch vor dem Ereignis
  { id: 'infoseiten', titel: 'Infoseiten', symbol: 'MdArticle', bereich: 'tools', slug: 'infoseiten', zugang: 'infoseiten', modul: 'infoseite', nurGebucht: true, suchbegriffe: ['infoseite', 'info', 'seite für helfende', 'webseite', 'vorab', 'informationen', 'reihe', 'zuordnen'] },
  // Planungsrechner (V6.7.0) — rechnen dürfen alle Verwaltungsrollen
  { id: 'planungsrechner', titel: 'Planungsrechner', symbol: 'MdCalculate', bereich: 'tools', slug: 'planungsrechner', zugang: 'planungsrechner', modul: 'planungsrechner', nurGebucht: true, suchbegriffe: ['maurer', 'kölner algorithmus', 'kräftebedarf', 'kräfte', 'bedarf', 'berechnung', 'rechner', 'sanitätswachdienst', 'besucher', 'wie viele helfer', 'rtw', 'ktw'] },
  // Kostenkalkulation (#615) — Ereignisse durchrechnen, bevor es sie gibt
  { id: 'kalkulationen', titel: 'Kalkulationen', symbol: 'MdRequestQuote', bereich: 'tools', slug: 'kalkulationen', zugang: 'kalkulationen', modul: 'kalkulation', nurGebucht: true, suchbegriffe: ['kosten', 'angebot', 'kalkulation', 'preis', 'was kostet'] },
];

export const ALLE_SEITEN: Seite[] = [...PERSONEN_SEITEN, ...VERWALTUNGS_SEITEN, ...WEITERE_SEITEN];

export const seiteMitId = (id: string): Seite | undefined => ALLE_SEITEN.find(s => s.id === id);

/** Adresse einer Seite — für „In der Verwaltung öffnen“ und Sprünge. */
export const seitenPfad = (s: Seite): string =>
  s.bereich === 'verwaltung' ? verwaltungsPfad(s) : s.bereich === 'personen' ? personenPfad(s)
    : (s.bereich === 'ereignisse' || s.bereich === 'tools') && s.slug ? `/${s.slug}` : `/${s.bereich}`;

/** Wo eine Seite liegt, in Worten („Verwaltung · System“, „Personen“). */
export const ortVon = (s: Seite): string => {
  if (s.bereich === 'verwaltung') return `Verwaltung · ${VERWALTUNGS_GRUPPEN.find(g => g.id === s.gruppe)?.titel ?? ''}`;
  if (s.bereich === 'personen') return 'Personen';
  if (s.bereich === 'tools') return `Tools · ${s.titel}`;
  return s.titel;
};
